import { useState } from 'react';
import { SYMBOL_SETS, SymbolSetKey } from '@/modules/calendar/model/data/data-impact-symbol-sets';

export function ZoomControls({
  zoomLevel,
  onChange,
}: {
  zoomLevel: '1m' | '10m' | '1h' | 'full';
  onChange: (z: '1m' | '10m' | '1h' | 'full') => void;
}) {
  return (
    <div className="btn-group" role="group" aria-label="zoom-controls">
      <button
        type="button"
        className={`btn btn-sm btn-outline-secondary${zoomLevel === '1m' ? ' active' : ''}`}
        onClick={() => onChange('1m')}
      >
        1m
      </button>
      <button
        type="button"
        className={`btn btn-sm btn-outline-secondary${zoomLevel === '10m' ? ' active' : ''}`}
        onClick={() => onChange('10m')}
      >
        10m
      </button>
      <button
        type="button"
        className={`btn btn-sm btn-outline-secondary${zoomLevel === '1h' ? ' active' : ''}`}
        onClick={() => onChange('1h')}
      >
        1hr
      </button>
      <button
        type="button"
        className={`btn btn-sm btn-outline-secondary${zoomLevel === 'full' ? ' active' : ''}`}
        onClick={() => onChange('full')}
      >
        Full
      </button>
    </div>
  );
}

export function SymbolSetSelector({
  symbolSet,
  onSelect,
  onHover,
  onLeave,
  menuOpen,
  setMenuOpen,
}: {
  symbolSet: SymbolSetKey;
  onSelect: (k: SymbolSetKey) => void;
  onHover: (k: SymbolSetKey | null) => void;
  onLeave: () => void;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  const [hoveredKeyLocal, setHoveredKeyLocal] = useState<SymbolSetKey | null>(null);

  // compute theme-based styles once so JSX stays simple
  const theme =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-bs-theme') === 'dark'
      ? 'dark'
      : 'light';
  const menuBg = theme === 'dark' ? '#1e1e1e' : 'white';
  const menuBorder =
    theme === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.15)';
  const menuShadow =
    theme === 'dark' ? '0 6px 12px rgba(0,0,0,0.6)' : '0 6px 12px rgba(0,0,0,0.175)';
  const textColor = theme === 'dark' ? '#eaeaea' : '#212529';

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(!menuOpen)}
        style={{
          minWidth: 220,
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{SYMBOL_SETS[symbolSet].label}</span>
        <span style={{ marginLeft: 8 }}>▾</span>
      </button>

      {menuOpen && (
        <div
          role="listbox"
          tabIndex={-1}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            background: menuBg,
            border: menuBorder,
            borderRadius: 4,
            marginTop: 6,
            zIndex: 200,
            boxShadow: menuShadow,
            minWidth: 220,
            color: textColor,
          }}
        >
          {Object.keys(SYMBOL_SETS).map(k => {
            const key = k as SymbolSetKey;
            const isSelected = key === symbolSet;
            const isHovered = hoveredKeyLocal === key;
            const itemBg = isSelected
              ? theme === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : 'rgba(0,0,0,0.03)'
              : isHovered
                ? theme === 'dark'
                  ? 'rgba(255,255,255,0.02)'
                  : 'rgba(0,0,0,0.02)'
                : 'transparent';

            return (
              <div
                key={key}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onSelect(key);
                  setMenuOpen(false);
                  setHoveredKeyLocal(null);
                }}
                onMouseEnter={() => {
                  setHoveredKeyLocal(key);
                  onHover(key);
                }}
                onMouseLeave={() => {
                  setHoveredKeyLocal(null);
                  onLeave();
                }}
                style={{ padding: '6px 8px', cursor: 'pointer', background: itemBg }}
              >
                {SYMBOL_SETS[key].label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SymbolsTooltip({ text, visible }: { text: string; visible: boolean }) {
  const theme =
    document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
  const bg = theme === 'dark' ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.95)';
  const color = theme === 'dark' ? 'white' : '#212529';
  const border =
    theme === 'dark' ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.08)';

  return (
    <div
      id={`symbols-tooltip`}
      role="tooltip"
      style={{
        position: 'absolute',
        top: -36,
        left: '0',
        right: '0',
        background: bg,
        color,
        padding: '6px 8px',
        borderRadius: 4,
        border,
        fontSize: 12,
        zIndex: 300,
        transition: 'opacity 0.2s ease-in-out',
        opacity: visible ? 1 : 0,
        visibility: visible ? 'visible' : 'hidden',
      }}
    >
      {text}
    </div>
  );
}
